// Code-action: "Add missing JOIN predicate" — Slice 12.
//
// Detects the legacy comma-list FROM cross-join shape (`FROM a, b`) and surfaces
// one quickfix per FK edge between the two tables — converting the comma into
// an explicit `INNER JOIN ... ON a.x = b.y` clause. Catches the most-common
// "forgot the JOIN condition" mistake before the user hits a Cartesian product.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 12.
//
// Strategy:
//   1. Parse the buffer + locate the cursor's enclosing SELECT statement.
//   2. Run `extractQueryStructure`. Trigger when:
//        - `fromItems.length >= 2` AND `joins.length === 0` (comma-list FROM
//          with no explicit JOIN — implicit cross-join).
//   3. For each PAIR of comma-list FROM relations, walk the OE tree to locate
//      either side's `keys-folder` looking for keys with
//      `keyType === 'FOREIGN KEY'` whose `columns[].referencedTable` matches
//      the other side. Each FK match produces ONE candidate edit.
//   4. The edit is a single contiguous byte replacement spanning
//      `[leftFromItem.endOffset, rightFromItem.endOffset)` — replaces the
//      `, <right text>` bytes with ` INNER JOIN <right text> ON <predicate>`.
//      This preserves the right-hand FROM item's source bytes (alias,
//      quoting style) verbatim.
//
// FK semantics:
//   The OE-tree's `key` node carries `KeyNodeMetadata.columns[]` where each
//   row pairs a local column (`name`) with the referenced remote column
//   (`referencedColumn`). The FK's owning table is the table whose Keys
//   folder the node lives under (the FK side; the referenced side is the PK
//   side). The predicate is built `<localRef>.<localCol> = <remoteRef>.<remoteCol>`
//   for every column pair, joined with AND.

import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import type { QueryStructure } from '../../../sql-resolver/types/queryStructure';
import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../../database-objects/store/databaseStore';
import { databaseEngineIdToSqlEngine } from '../../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { defaultSchemaForEngine } from '../../go-to/externalNavigation';
import {
  findForeignKeyPairsInSnapshot,
  mergeForeignKeyResults,
  requestForeignKeyLoad,
} from './foreignKeySource';
import type {
  CodeActionContext,
  CodeActionEdit,
  CodeActionRegistryEntry,
} from '../../types';

const ENTRY_ID = 'add-missing-join-predicate' as const;

export const addMissingJoinPredicateEntry: CodeActionRegistryEntry = {
  id: ENTRY_ID,
  kind: 'quickfix',
  title: 'Add missing JOIN predicate',
  canApply: (ctx) => collectFixes(ctx, readLiveTree).length > 0,
  buildEdits: (ctx) => collectFixes(ctx, readLiveTree),
};

/** Test seam — lets unit tests pass a hand-built OE tree without touching the
 *  live Zustand store. Mirrors Slice 11's `collectQualifyAmbiguousColumnEditsForTest`. */
export function collectAddMissingJoinPredicateEditsForTest(
  ctx: CodeActionContext,
  getTree: () => readonly DatabaseExplorerNode[],
): readonly CodeActionEdit[] {
  return collectFixes(ctx, getTree);
}

type FromRelation = {
  readonly index: number;
  readonly endOffset: number;
  readonly startOffset: number;
  readonly schema: string;
  /** The schema as the USER typed it (lower-cased), or `undefined` when the
   *  user typed an unqualified name. Drives permissive OE-tree schema match
   *  — see Slice 6 / 11's `lookupRelationWithColumn` / `relationOwnsColumn`
   *  fix: when the user didn't type a schema, accept any OE-tree schema
   *  rather than strict-comparing against `defaultSchemaForEngine`, which
   *  three of five engines diverge from on the OE side
   *  (mysql=`''`, oracle login uppercase, sqlite=`'main'`). */
  readonly userTypedSchema?: string;
  readonly name: string;
  readonly displayRef: string;
};

type FkPair = {
  readonly localCol: string;
  readonly remoteCol: string;
};

function collectFixes(
  ctx: CodeActionContext,
  getTree: () => readonly DatabaseExplorerNode[],
): readonly CodeActionEdit[] {
  const source = ctx.sourceText;
  if (!source || source.trim().length === 0) return [];
  const connectionId = ctx.connectionId;
  if (!connectionId) return [];

  const sqlEngine = databaseEngineIdToSqlEngine(ctx.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  const buffer = parseBuffer(source, options);
  if (!buffer) return [];
  const stmt = buffer.documentStructure.statementAt(ctx.cursor.offset)
    ?? buffer.documentStructure.statements[0]
    ?? null;
  if (!stmt) return [];

  let structure: QueryStructure;
  try {
    structure = extractQueryStructure(buffer, stmt);
  } catch {
    return [];
  }
  if (structure.kind !== 'select') return [];

  // Trigger: 2+ comma-list FROM items AND no explicit JOIN.
  const fromItems = structure.fromItems ?? [];
  const joins = structure.joins ?? [];
  if (fromItems.length < 2 || joins.length > 0) return [];

  const relations = collectFromRelations(fromItems, ctx.engineId);
  if (relations.length < 2) return [];

  const treeData = getTree();
  const defaultSchema = defaultSchemaForEngine(ctx.engineId).toLowerCase();
  const lineIdx = buildLineIndex(source);

  // Walk every adjacent pair `(relations[i], relations[i+1])`. Most queries
  // have exactly 2 comma-list FROM items, so iterating pairs is the right
  // grain — the user picks which adjacent comma to convert.
  const edits: CodeActionEdit[] = [];
  for (let i = 0; i + 1 < relations.length; i++) {
    const left = relations[i];
    const right = relations[i + 1];

    // Direction A: FK lives on `left`, references `right`.
    const fksOnLeft = findForeignKeys(
      treeData,
      ctx.engineId,
      connectionId,
      ctx.database ?? null,
      left,
      right,
      defaultSchema,
    );
    for (const fk of fksOnLeft) {
      edits.push(buildEdit(source, lineIdx, left, right, fk, /* fkSide = */ 'left'));
    }

    // Direction B: FK lives on `right`, references `left`.
    const fksOnRight = findForeignKeys(
      treeData,
      ctx.engineId,
      connectionId,
      ctx.database ?? null,
      right,
      left,
      defaultSchema,
    );
    for (const fk of fksOnRight) {
      edits.push(buildEdit(source, lineIdx, left, right, fk, /* fkSide = */ 'right'));
    }
  }

  // Deterministic ordering: sort by description so multi-FK output is stable.
  edits.sort((a, b) => (a.description < b.description ? -1 : a.description > b.description ? 1 : 0));
  return edits;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function buildEdit(
  source: string,
  lineIdx: ReturnType<typeof buildLineIndex>,
  left: FromRelation,
  right: FromRelation,
  pairs: readonly FkPair[],
  fkSide: 'left' | 'right',
): CodeActionEdit {
  const conds = pairs.map((p) =>
    fkSide === 'left'
      ? `${left.displayRef}.${p.localCol} = ${right.displayRef}.${p.remoteCol}`
      : `${right.displayRef}.${p.localCol} = ${left.displayRef}.${p.remoteCol}`,
  );
  const onText = conds.join(' AND ');
  const rightItemText = source.slice(right.startOffset, right.endOffset);

  return {
    id: ENTRY_ID,
    description: `Add JOIN ON ${onText}`,
    textEdits: [
      {
        range: rangeFromOffsets(lineIdx, left.endOffset, right.endOffset),
        newText: ` INNER JOIN ${rightItemText} ON ${onText}`,
      },
    ],
  };
}

function collectFromRelations(
  fromItems: readonly NonNullable<QueryStructure['fromItems']>[number][],
  engineId: CodeActionContext['engineId'],
): readonly FromRelation[] {
  const out: FromRelation[] = [];
  for (let i = 0; i < fromItems.length; i++) {
    const fi = fromItems[i];
    if (fi.target.kind !== 'relation') continue;
    const qn = fi.target.qualified;
    const alias = fi.alias?.alias;
    const userTypedSchema = qn.schema?.toLowerCase();
    out.push({
      index: i,
      startOffset: fi.range.startOffset,
      endOffset: fi.range.endOffset,
      schema: (qn.schema ?? defaultSchemaForEngine(engineId)).toLowerCase(),
      userTypedSchema,
      name: qn.name,
      displayRef: alias ?? qn.name,
    });
  }
  return out;
}

function parseBuffer(source: string, options: ParseOptions): ParsedBuffer | null {
  try {
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'add-missing-join-predicate');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

function readLiveTree(): readonly DatabaseExplorerNode[] {
  return useDatabaseStore.getState().treeData;
}

// All FK constraints that live on `localRel` AND reference `remoteRel`, from
// both sources (see `foreignKeySource.ts`): the OE tree's `keys-folder` when
// the user happens to have loaded it, and the language service's schema
// snapshot otherwise / in addition. Each return entry is the column pair list
// of one FK constraint; multi-column FKs surface as ONE entry with N pairs.
// A cold snapshot kicks the engine's schema load so the next compute has it.
function findForeignKeys(
  treeData: readonly DatabaseExplorerNode[],
  engineId: CodeActionContext['engineId'],
  connectionId: string,
  database: string | null,
  localRel: FromRelation,
  remoteRel: FromRelation,
  defaultSchema: string,
): readonly (readonly FkPair[])[] {
  const fromTree = findForeignKeysInTree(treeData, connectionId, database, localRel, remoteRel, defaultSchema);
  const fromSnapshot = findForeignKeyPairsInSnapshot({
    connectionId,
    database,
    localName: localRel.name,
    localSchemaForMatch: localRel.userTypedSchema,
    remoteName: remoteRel.name,
    remoteSchemaForMatch: remoteRel.userTypedSchema,
    defaultSchema,
  });
  requestForeignKeyLoad(engineId, connectionId, database);
  return mergeForeignKeyResults(fromTree, fromSnapshot);
}

function findForeignKeysInTree(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  localRel: FromRelation,
  remoteRel: FromRelation,
  defaultSchema: string,
): readonly (readonly FkPair[])[] {
  const tableNode = findTableNode(treeData, connectionId, database, localRel);
  if (!tableNode) return [];
  const keysFolder = (tableNode.children ?? []).find((c) => c.type === 'keys-folder');
  if (!keysFolder) return [];

  const out: (readonly FkPair[])[] = [];
  for (const keyNode of keysFolder.children ?? []) {
    if (keyNode.type !== 'key') continue;
    const meta = keyNode.metadata as
      | {
          keyType?: 'PRIMARY KEY' | 'FOREIGN KEY' | 'UNIQUE';
          columns?: readonly {
            name: string;
            referencedSchema?: string;
            referencedTable?: string;
            referencedColumn?: string;
          }[];
        }
      | undefined;
    if (meta?.keyType !== 'FOREIGN KEY') continue;
    const cols = meta.columns ?? [];
    if (cols.length === 0) continue;

    // All FK column rows must reference the same `remoteRel`.
    const refTable = cols[0].referencedTable;
    const refSchema = cols[0].referencedSchema;
    if (!refTable || refTable.toLowerCase() !== remoteRel.name.toLowerCase()) continue;
    // Permissive schema match (Slice 6 / 11 pattern): when the user typed an
    // unqualified relation, accept any FK referencedSchema; only enforce the
    // filter when the user typed a schema AND the FK ref disagrees with both
    // it and the engine's default. Three of five engines project a schema
    // label that diverges from `defaultSchemaForEngine` on the OE side
    // (mysql=`''` / oracle login uppercase / sqlite=`'main'`), so the strict
    // legacy compare blocked the unqualified-cursor fix on those engines.
    if (
      remoteRel.userTypedSchema &&
      refSchema &&
      refSchema.toLowerCase() !== remoteRel.userTypedSchema &&
      refSchema.toLowerCase() !== defaultSchema
    ) {
      // Schema mismatch — different referenced table.
      continue;
    }

    // Build the column pairs. Skip rows missing referencedColumn.
    const pairs: FkPair[] = [];
    for (const c of cols) {
      if (!c.referencedColumn) continue;
      pairs.push({ localCol: c.name, remoteCol: c.referencedColumn });
    }
    if (pairs.length === 0) continue;
    out.push(pairs);
  }
  return out;
}

function findTableNode(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  rel: FromRelation,
): DatabaseExplorerNode | null {
  let result: DatabaseExplorerNode | null = null;
  walk(treeData, undefined);
  return result;

  function walk(nodes: readonly DatabaseExplorerNode[] | undefined, currentDb: string | undefined): void {
    if (!nodes || result) return;
    for (const node of nodes) {
      if (result) return;
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === 'table') {
        const meta = node.metadata as { objectDetails?: { name?: string; schema?: string } } | undefined;
        const od = meta?.objectDetails;
        if (od?.name && od.name.toLowerCase() === rel.name.toLowerCase()) {
          if (database && (currentDb ?? '').toLowerCase() !== database.toLowerCase()) {
            walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
            continue;
          }
          // Permissive schema match (Slice 6 / 11 pattern): when the user
          // typed an unqualified relation, accept any OE-tree schema; only
          // enforce strict equality when the user explicitly typed a schema
          // part. Three of five engines diverge from `defaultSchemaForEngine`
          // on the OE side (mysql=`''`, oracle login uppercase, sqlite=`'main'`).
          if (rel.userTypedSchema && (od.schema ?? '').toLowerCase() !== rel.userTypedSchema) {
            walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
            continue;
          }
          result = node;
          return;
        }
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}
